import { getConfig } from '../config/environment';

// Use runtime environment detection to switch between dev and prod APIs
const config = getConfig();
const API_BASE_URL = config.apiUrl;

export interface Ticket {
  ticket_id: string;
  tenant_id: string;
  user_id: string;
  user_email: string;
  issue_description: string;
  summary?: string;
  description?: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'critical' | 'high' | 'medium' | 'low';
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
  ticket_number?: string;
  estimated_resolution?: string;
}

export interface CreateTicketRequest {
  issue_description: string;
  summary?: string;
  description?: string;
  priority?: 'critical' | 'high' | 'medium' | 'low';
  assigned_to?: string;
  metadata?: Record<string, any>;
}

class TicketsService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('auth_token');
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getTickets(): Promise<Ticket[]> {
    return this.request<Ticket[]>('/api/v1/tickets');
  }

  async listMyTickets(): Promise<Ticket[]> {
    return this.request<Ticket[]>('/api/v1/tickets?assigned_only=true');
  }

  async getTicket(ticketId: string): Promise<Ticket> {
    return this.request<Ticket>(`/api/v1/tickets/${ticketId}`);
  }

  async createTicket(ticket: CreateTicketRequest): Promise<Ticket> {
    return this.request<Ticket>('/api/v1/tickets', {
      method: 'POST',
      body: JSON.stringify(ticket),
    });
  }

  async updateTicket(ticketId: string, updates: Partial<Ticket>): Promise<Ticket> {
    return this.request<Ticket>(`/api/v1/tickets/${ticketId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteTicket(ticketId: string): Promise<void> {
    return this.request<void>(`/api/v1/tickets/${ticketId}`, {
      method: 'DELETE',
    });
  }

  async addComment(ticketId: string, comment: string): Promise<any> {
    return this.request(`/api/v1/tickets/${ticketId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  async uploadAttachment(
    ticketId: string,
    file: File,
    title: string,
    description: string,
    artifactType: string = 'file_attachment'
  ): Promise<any> {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('artifact_type', artifactType);

    const url = `${API_BASE_URL}/api/v1/tickets/${ticketId}/attachments/upload`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getTicketHistory(ticketId: string): Promise<any[]> {
    return this.request<any[]>(`/api/v1/tickets/${ticketId}/history`);
  }

  async getTicketArtifacts(ticketId: string): Promise<any[]> {
    return this.request<any[]>(`/api/v1/tickets/${ticketId}/artifacts`);
  }

  async getTicketNotes(ticketId: string): Promise<any[]> {
    return this.request<any[]>(`/api/v1/tickets/${ticketId}/notes`);
  }

  async getComments(ticketId: string): Promise<any[]> {
    return this.request<any[]>(`/api/v1/tickets/${ticketId}/comments`);
  }

  async uploadTicketAttachment(
    ticketId: string,
    file: File,
    title: string,
    description: string,
    artifactType: string = 'file_attachment'
  ): Promise<any> {
    return this.uploadAttachment(ticketId, file, title, description, artifactType);
  }

  async addTicketArtifact(ticketId: string, artifactData: any): Promise<any> {
    return this.request(`/api/v1/tickets/${ticketId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify(artifactData),
    });
  }

  async generateAIClosingSummary(ticketId: string): Promise<any> {
    return this.request(`/api/v1/tickets/${ticketId}/ai-closing-summary`, {
      method: 'POST',
    });
  }

  async getSimilarTickets(ticketId: string, limit: number = 5): Promise<any> {
    return this.request(`/api/v1/tickets/${ticketId}/similar?limit=${limit}`);
  }

  async getTicketInsights(ticketId: string): Promise<any> {
    return this.request(`/api/v1/tickets/${ticketId}/insights`);
  }
}

export const ticketsService = new TicketsService();
